import axios from 'axios';
import { ENDPOINTS } from '../constants/endpoints';
import {
  AdjustmentRequestDTO, BankReconciliation, BankReconciliationWorkspace,
  BankStatementLine, BankStatementLineCreateDTO, CreateReconciliationDTO,
  MatchRequestDTO, StatementImportResult, UnmatchRequestDTO, UnreconciledReport,
} from '../interfaces/bank/bankReconciliation.interface';

export const BankReconciliationService = {
  listByAccount: async (bankAccountId: string): Promise<BankReconciliation[]> =>
    (await axios.get(ENDPOINTS.BANK_RECONCILIATION.LIST_BY_ACCOUNT(bankAccountId))).data,
  getWorkspace: async (id: string): Promise<BankReconciliationWorkspace> =>
    (await axios.get(ENDPOINTS.BANK_RECONCILIATION.WORKSPACE(id))).data,
  create: async (dto: CreateReconciliationDTO): Promise<BankReconciliation> =>
    (await axios.post(ENDPOINTS.BANK_RECONCILIATION.CREATE, dto)).data,
  remove: async (id: string): Promise<void> => {
    await axios.delete(ENDPOINTS.BANK_RECONCILIATION.DELETE(id));
  },
  importStatement: async (id: string, file: File): Promise<StatementImportResult> => {
    const formData = new FormData();
    formData.append('file', file);
    return (await axios.post(ENDPOINTS.BANK_RECONCILIATION.IMPORT(id), formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })).data;
  },
  addLine: async (id: string, dto: BankStatementLineCreateDTO): Promise<BankStatementLine> =>
    (await axios.post(ENDPOINTS.BANK_RECONCILIATION.ADD_LINE(id), dto)).data,
  editLine: async (lineId: string, dto: BankStatementLineCreateDTO): Promise<BankStatementLine> =>
    (await axios.put(ENDPOINTS.BANK_RECONCILIATION.EDIT_LINE(lineId), dto)).data,
  removeLine: async (lineId: string): Promise<void> => {
    await axios.delete(ENDPOINTS.BANK_RECONCILIATION.REMOVE_LINE(lineId));
  },
  match: async (id: string, dto: MatchRequestDTO): Promise<void> => {
    await axios.post(ENDPOINTS.BANK_RECONCILIATION.MATCH(id), dto);
  },
  unmatch: async (id: string, dto: UnmatchRequestDTO): Promise<void> => {
    await axios.post(ENDPOINTS.BANK_RECONCILIATION.UNMATCH(id), dto);
  },
  createAdjustment: async (id: string, dto: AdjustmentRequestDTO): Promise<void> => {
    await axios.post(ENDPOINTS.BANK_RECONCILIATION.ADJUSTMENT(id), dto);
  },
  complete: async (id: string): Promise<BankReconciliation> =>
    (await axios.post(ENDPOINTS.BANK_RECONCILIATION.COMPLETE(id))).data,
  reopen: async (id: string): Promise<BankReconciliation> =>
    (await axios.post(ENDPOINTS.BANK_RECONCILIATION.REOPEN(id))).data,
  getUnreconciledReport: async (bankAccountId: string, asOf: string): Promise<UnreconciledReport> =>
    (await axios.get(ENDPOINTS.BANK_RECONCILIATION.UNRECONCILED(bankAccountId, asOf))).data,
  downloadUnreconciledXlsx: async (bankAccountId: string, asOf: string): Promise<Blob> =>
    (await axios.get(ENDPOINTS.BANK_RECONCILIATION.UNRECONCILED_XLSX(bankAccountId, asOf), {
      responseType: 'blob',
    })).data,
  // T10: the report page's Download XLSX button needs the server-named filename off
  // Content-Disposition (BankReconciliationController#unreconciledReportXlsx sets it), which the
  // blob-only downloadUnreconciledXlsx above discards -- so this variant returns the full
  // AxiosResponse instead of just its .data.
  downloadUnreconciledXlsxResponse: async (bankAccountId: string, asOf: string) =>
    axios.get<Blob>(ENDPOINTS.BANK_RECONCILIATION.UNRECONCILED_XLSX(bankAccountId, asOf), {
      responseType: 'blob',
    }),
};
